// Base configuration template for all import types
// This provides the structure and default implementations

export const createBaseImportConfig = ({
  entityName,
  entityNamePlural,
  requiredFields = [],
  optionalFields = [],
  fields = {},
  tableColumns = [],
  api = {},
  statusConfig = {},
  documentation = {},
  helpers = {},
  hooks = {}
}) => {
  // Default status configurations
  const defaultStatusConfig = {
    validationStatuses: {
      'valid': { label: 'Valid', color: '#10B981' },
      'invalid': { label: 'Invalid', color: '#EF4444' },
      'pending': { label: 'Pending', color: '#6366F1' }
    },
    importStatuses: {
      'success': { label: 'Imported', color: '#059669', backgroundColor: '#ECFDF5' },
      'failed': { label: 'Import Failed', color: '#DC2626', backgroundColor: '#FEF2F2' }
    },
    ...statusConfig
  };

  // Default helpers
  const defaultHelpers = {
    // Get items that can be imported (override for custom logic)
    getImportableItems: (items) => {
      return items.filter(item => item.validationStatus === 'valid');
    },
    
    // Get status counts (override for entity-specific counts)
    getStatusCounts: (items) => {
      return {
        total: items.length,
        valid: items.filter(item => item.validationStatus === 'valid').length,
        invalid: items.filter(item => item.validationStatus === 'invalid').length,
        pending: items.filter(item => item.validationStatus === 'pending').length,
        importable: helpers.getImportableItems?.(items).length || 
                    items.filter(item => item.validationStatus === 'valid').length
      };
    },
    
    // Process validation response (override for custom processing)
    processValidationResponse: (response) => {
      return response.rows.map((row, index) => ({
        ...row.data,
        id: `temp_${Date.now()}_${index}`,
        validationStatus: row.validationStatus || 'valid',
        validationErrors: row.validationErrors || [],
        editable: row.editable !== false,
        rowIndex: row.rowIndex || index
      }));
    },
    
    // Process import response (override for custom processing)
    processImportResponse: (response, data, itemsToImport) => {
      const errorMap = {};
      if (response.errors) {
        response.errors.forEach(error => {
          errorMap[error.rowIndex] = error;
        });
      }
      return data.map(item => {
        if (!itemsToImport.some(importItem => importItem.id === item.id)) {
          return item; // Keep non-importable items as-is
        }
        
        const result = response.results?.find(r => r.rowIndex === item.rowIndex);
        const error = errorMap[item.rowIndex];
        
        if (error) {
          return {
            ...item,
            validationStatus: 'invalid',
            validationErrors: [
              ...(item.validationErrors || []),
              { field: 'import', message: error.message }
            ],
            importStatus: 'failed',
            importError: error.message
          };
        } else if (result && result.status === 'success') {
          return {
            ...item,
            importStatus: 'success',
            importedItem: result[entityName.toLowerCase()]
          };
        }
        
        return item;
      });
    },
    
    // Get import tooltip message (override for custom messages)
    getImportTooltip: (_, config) => {
      // Default implementation - just return the generic message
      return `No ${config.entityNamePlural.toLowerCase()} ready to import`;
    },
    
    ...helpers
  };

  // Ensure tableColumns includes all fields if not specified
  const defaultTableColumns = tableColumns.length > 0 ? tableColumns : [
    { field: 'status', label: 'Status', width: 120 },
    ...Object.entries(fields).map(([field, config]) => ({
      field,
      label: config.label,
      width: config.width || 150
    })),
    { field: 'actions', label: 'Actions', width: 80 }
  ];

  // Default hooks for lifecycle events
  const defaultHooks = {
    // Called after validation response is processed
    onPostValidation: () => {
      // Default: no-op
    },
    
    // Called when a field value changes
    onFieldChange: () => {
      // Default: no-op
    },
    
    // Called to get custom state requirements
    getCustomState: () => ({}),
    
    // Called to initialize custom state in the store
    initializeCustomState: () => {
      // Default: no-op
    },
    
    // Called to get extended field context for EditableCell
    getFieldContext: () => ({}),
    
    ...hooks
  };

  return {
    entityName,
    entityNamePlural: entityNamePlural || `${entityName}s`,
    requiredFields,
    optionalFields,
    fields,
    tableColumns: defaultTableColumns,
    api: {
      validate: () => Promise.reject(new Error('Validate API not implemented')),
      import: () => Promise.reject(new Error('Import API not implemented')),
      ...api
    },
    statusConfig: defaultStatusConfig,
    documentation: {
      description: `Upload a CSV file with ${entityName.toLowerCase()} data to import into the system`,
      fileFormat: {
        dateFormat: 'YYYY-MM-DD',
        notes: []
      },
      ...documentation
    },
    helpers: defaultHelpers,
    hooks: defaultHooks
  };
};

// Field type definitions for common field types
export const FieldTypes = {
  TEXT: 'text',
  DATE: 'date',
  NUMBER: 'number',
  EMAIL: 'email',
  SELECTOR: 'selector',
  BOOLEAN: 'boolean',
  MULTI_SELECTOR: 'multi_text'
};

// Common field configurations
export const CommonFields = {
  name: (label = 'Name') => ({
    type: FieldTypes.TEXT,
    label,
    required: true
  }),
  
  email: (label = 'Email') => ({
    type: FieldTypes.EMAIL,
    label,
    required: true
  }),
  
  date: (label = 'Date', required = true) => ({
    type: FieldTypes.DATE,
    label,
    required
  }),
  
  description: (label = 'Description', required = false) => ({
    type: FieldTypes.TEXT,
    label,
    required
  }),
  
  code: (label = 'Code', required = true) => ({
    type: FieldTypes.TEXT,
    label,
    required
  }),
  
  clientSelector: (label = 'Client', required = true) => ({
    type: FieldTypes.SELECTOR,
    label,
    required,
    component: null, // Will be set in the specific import config
    returnType: 'id'
  })
};